/**
 * Turns a caught auth failure into the three things the form needs to show it
 * (EQU-118): the prose, the field to mark, and — when the prose alone leaves
 * the user stuck — where to go next.
 *
 * Both auth pages reported every failure through `notification.error` and
 * nothing else. An antd notification auto-dismisses after 4.5s, so the form was
 * left looking untouched and people clicked the button again: 26 "Create
 * account" clicks from 16 people, and 20 rageclicks on `/signin` from 8.
 *
 * 🪤 The code, not the message, decides the field. `getErrorMsg` descriptions
 * are human prose that a Content pass is expected to reword, and two different
 * codes already collapse onto the same sentence ("Invalid email or password"
 * covers both `auth/user-not-found` and `auth/wrong-password`). Keying the
 * mapping on prose would silently re-point the marked field the next time
 * someone edits a string.
 */

import { signupFailureReason } from "utils/activationAnalytics";
import { getErrorMsg } from "utils/geterror";

export type AuthFormField = "email" | "password";

export type AuthFormError = {
	/** The refusal, verbatim from `getErrorMsg` — the same text the toast shows. */
	description: string;
	/** The field the refusal is attributable to, or null when it is not. */
	field: AuthFormField | null;
	/** Where the user goes next, when the refusal alone does not say. */
	nextAction: { to: string; label: string } | null;
};

/**
 * Only the codes EQU-118 named. `auth/user-not-found` is deliberately absent:
 * it resolves to "Invalid email or password", which does not attribute the
 * failure to either field, so marking one would be a guess shown as a fact.
 */
const FIELD_BY_CODE: Record<string, AuthFormField> = {
	"auth/email-already-in-use": "email",
	"auth/invalid-email": "email",
	"auth/weak-password": "password",
	"auth/wrong-password": "password",
	"auth/invalid-credential": "password",
};

const RESEND_VERIFICATION = {
	to: "/verify-email",
	label: "Resend the verification email",
};

/**
 * The one refusal that is about verification rather than credentials.
 *
 * `POST /auth/accept-invite` answers 403 with this exact prose and no
 * machine-readable code (`equility-api` `auth.service.ts` -> `acceptInvite`),
 * so the message is the only key available. Matched exactly, the way
 * `utils/authError.ts` keys its own message checks — a reworded server string
 * then fails closed to "no next action" instead of attaching the wrong one.
 */
const EMAIL_NOT_VERIFIED = "Email must be verified";

export function toAuthFormError(error: unknown): AuthFormError {
	const { description } = getErrorMsg(error);

	// `signupFailureReason` is reused rather than re-derived: it is already the
	// one place that knows where a code hides (Firebase `code`, the API's nested
	// `message.code`, flat `code`), and sharing it means the field this marks and
	// the `reason` on `signup_failed` can never disagree about the same error.
	return {
		description,
		field: FIELD_BY_CODE[signupFailureReason(error)] ?? null,
		nextAction: description === EMAIL_NOT_VERIFIED ? RESEND_VERIFICATION : null,
	};
}
